import AbstractApiService from '../abstractApiService';
import { api } from 'pages/APIs';
import type {
  IdeasListResponse,
  IdeaSingleResponse,
  CreateIdeaPayload,
  UpdateIdeaPayload,
  VoteResponse,
  VoteDirection,
  CommentVoteResponse,
  MyVotesResponse,
  CommentsListResponse,
  CreateCommentPayload,
  ChannelsResponse,
  AssetsListResponse,
  PendingCommentsResponse,
  PendingIdeasResponse,
  TrustedOrgsResponse,
} from 'src/types/api/ideas';

export default class IdeasApiService extends AbstractApiService {
  public constructor() {
    super({});
  }

  public async getIdeas(params: {
    page?: number;
    page_size?: number;
    category?: string;
    channel?: string;
    status?: string;
    search?: string;
    sort?: string;
  }): Promise<IdeasListResponse> {
    const query = new URLSearchParams();
    if (params.page) query.append('page', params.page.toString());
    if (params.page_size) query.append('page_size', params.page_size.toString());
    if (params.category) query.append('category', params.category);
    if (params.channel) query.append('channel', params.channel);
    if (params.status) query.append('status', params.status);
    if (params.search) query.append('search', params.search);
    if (params.sort) query.append('sort', params.sort);

    return super.get(`${api.ideas_list}?${query.toString()}`);
  }

  public async getChannels(): Promise<ChannelsResponse> {
    return super.get(`${api.ideas_list}/channels`);
  }

  public async getIdea(id: string): Promise<IdeaSingleResponse> {
    return super.get(`${api.ideas_list}/${id}`);
  }

  public async createIdea(payload: CreateIdeaPayload): Promise<IdeaSingleResponse> {
    return super.post(api.ideas_list, payload);
  }

  public async voteOnIdea(ideaId: string, direction: VoteDirection): Promise<VoteResponse> {
    return super.post(`${api.ideas_list}/${ideaId}/vote`, { direction });
  }

  public async updateStatus(ideaId: string, status: string): Promise<IdeaSingleResponse> {
    return super.patch(`${api.ideas_list}/${ideaId}/status`, { status });
  }

  public async deleteIdea(ideaId: string): Promise<{ success: boolean }> {
    return super.delete(`${api.ideas_list}/${ideaId}`, {});
  }

  public async getMyVotes(): Promise<MyVotesResponse> {
    return super.get(api.ideas_my_votes);
  }

  public async updateIdea(ideaId: string, payload: UpdateIdeaPayload): Promise<IdeaSingleResponse> {
    return super.put(`${api.ideas_list}/${ideaId}`, payload);
  }

  public async getComments(ideaId: string): Promise<CommentsListResponse> {
    return super.get(`${api.ideas_list}/${ideaId}/comments`);
  }

  public async createComment(ideaId: string, payload: CreateCommentPayload): Promise<CommentsListResponse> {
    return super.post(`${api.ideas_list}/${ideaId}/comments`, payload);
  }

  public async deleteComment(ideaId: string, commentId: string): Promise<{ success: boolean }> {
    return super.delete(`${api.ideas_list}/${ideaId}/comments/${commentId}`, {});
  }

  public async voteOnComment(ideaId: string, commentId: string, direction: VoteDirection): Promise<CommentVoteResponse> {
    return super.post(`${api.ideas_list}/${ideaId}/comments/${commentId}/vote`, { direction });
  }

  public async uploadAsset(file: File, ideaId?: string): Promise<{
    success: boolean;
    asset: { id: string; url: string; filename: string; mime_type: string };
  }> {
    const formData = new FormData();
    formData.append('file', file);
    if (ideaId) formData.append('idea_id', ideaId);
    return super.post(api.ideas_upload, formData);
  }

  public async getAssetsByIdea(ideaId: string): Promise<AssetsListResponse> {
    return super.get(`${api.ideas_list}/assets/by-idea/${ideaId}`);
  }

  public async deleteAsset(assetId: string): Promise<{ success: boolean }> {
    return super.delete(`${api.ideas_list}/assets/${assetId}`, {});
  }

  /**
   * Uploads an image file for inline embedding in an idea description (rich text editor).
   * Use this when the idea does not exist yet — no ideaId is required.
   * For post-creation file attachments, use uploadAsset(file, ideaId) instead.
   *
   * @throws {Error} if the file is not an image MIME type
   */
  public async uploadImage(file: File): Promise<{
    success: boolean;
    asset: { id: string; url: string; filename: string; mime_type: string };
  }> {
    if (!file.type.startsWith('image/')) {
      throw new Error(`uploadImage only accepts image files, got: ${file.type}`);
    }
    return this.uploadAsset(file);
  }

  // Moderation endpoints

  public async checkModerationAccess(): Promise<{ success: boolean; has_access: boolean }> {
    return super.get(`${api.ideas_list}/moderation/access`);
  }

  public async getPendingComments(): Promise<PendingCommentsResponse> {
    return super.get(`${api.ideas_list}/moderation/pending`);
  }

  public async moderateComment(commentId: string, action: 'approve' | 'reject' | 'hide'): Promise<{ success: boolean }> {
    return super.patch(`${api.ideas_list}/moderation/comments/${commentId}`, { action });
  }

  public async getPendingIdeas(): Promise<PendingIdeasResponse> {
    return super.get(`${api.ideas_list}/moderation/ideas/pending`);
  }

  public async moderateIdea(ideaId: string, action: 'approve' | 'reject'): Promise<IdeaSingleResponse> {
    return super.patch(`${api.ideas_list}/moderation/ideas/${ideaId}`, { action });
  }

  public async getTrustedOrgs(): Promise<TrustedOrgsResponse> {
    return super.get(`${api.ideas_list}/moderation/trusted-orgs`);
  }

  public async addTrustedOrg(orgId: string, orgName: string): Promise<{ success: boolean }> {
    return super.post(`${api.ideas_list}/moderation/trusted-orgs`, { org_id: orgId, org_name: orgName });
  }

  public async removeTrustedOrg(id: string): Promise<{ success: boolean }> {
    return super.delete(`${api.ideas_list}/moderation/trusted-orgs/${id}`, {});
  }
}
